import type { ReactNode } from 'react'
import { Check } from 'lucide-react'
import { format } from 'date-fns'
import type { MeetingAppName } from '@highlight/common'
import { ProviderIcon } from '@renderer/components/provider-icon'
import { useProviders } from '@renderer/hooks/use-providers'
import type { StagedAction } from '@renderer/overlay/Sidepanel/hooks/use-staged-actions'
import type { RecentActivity } from '@renderer/overlay/Sidepanel/types'
import { Logo, HighlightLogo } from '@highlight/ui'
import { providerLogo } from '@renderer/utils/provider-logo'

export const KNOWN_MEETING_APPS: Array<MeetingAppName> = ['Google Meet', 'Zoom', 'Microsoft Teams', 'Huddle']

/**
 * A meeting's clock range — "10:00–10:30am". The meridiem is dropped from the
 * start when both ends share it, which is the common case and reads tighter.
 */
export function formatTimeRange(start: Date, end?: Date): string {
  const startPeriod = start.getHours() < 12 ? 'a' : 'p'
  const endPeriod = end ? (end.getHours() < 12 ? 'a' : 'p') : null
  const samePeriod = endPeriod === startPeriod

  const startStr = format(start, samePeriod ? 'h:mm' : 'h:mmaaa')
  const endStr = end ? format(end, 'h:mmaaa') : null

  return endStr ? `${startStr}–${endStr}` : startStr
}

interface ActionIconSource {
  actionType?: string
  toolSet?: string
  view?: {
    icon?: string
    icons?: Array<string>
  }
}

/**
 * Formats a future date as a relative time string (e.g., "Now", "in 5m", "in 2h", "in 3d")
 * When endDate is provided and has passed, returns "ended Xm ago"
 */
export function formatTimeUntil(date: Date, endDate?: Date): string {
  const now = new Date()

  if (endDate) {
    const endDiffMs = now.getTime() - endDate.getTime()
    if (endDiffMs > 0) {
      const endedMinsAgo = Math.floor(endDiffMs / 60000)
      if (endedMinsAgo < 1) return 'ended just now'
      return `ended ${endedMinsAgo}m ago`
    }
  }

  const diffMs = date.getTime() - now.getTime()

  if (diffMs < 0) return 'Now'

  const diffMins = Math.floor(diffMs / 60000)

  if (diffMins < 1) return 'Now'
  if (diffMins < 60) return `in ${diffMins}m`

  const diffHours = Math.round(diffMins / 60)
  if (diffHours < 24) return `in ${diffHours}h`

  const diffDays = Math.round(diffMins / 1440)
  return `in ${diffDays}d`
}

/**
 * Formats elapsed time between two dates as "Xm Ys" (e.g., "1m 12s", "2h 30m").
 * If endDate is omitted, measures from startDate to now.
 * Returns null when the elapsed time is less than 1 second.
 */
export function formatElapsedTime(startDate: string | Date, endDate?: string | Date | null): string | null {
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate
  const end = endDate ? (typeof endDate === 'string' ? new Date(endDate) : endDate) : new Date()
  const elapsedMs = Math.max(0, end.getTime() - start.getTime())
  const totalSeconds = Math.floor(elapsedMs / 1000)

  if (totalSeconds < 1) return null

  if (totalSeconds < 60) {
    return `${totalSeconds}s`
  }

  if (totalSeconds < 3600) {
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    return `${minutes}m ${seconds}s`
  }

  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  return `${hours}h ${minutes}m`
}

/**
 * Formats a date as a relative time string (e.g., "Just now", "5m ago", "2h ago", "3d ago")
 */
const absoluteDateFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
})

/**
 * The rungs, largest unit last — each is "how many of this unit, and when to
 * stop counting in it". One ladder so a threshold can only be changed in one
 * place.
 */
const TIME_AGO_RUNGS = [
  { unit: 'minute' as const, perUnit: 60_000, until: 60 },
  { unit: 'hour' as const, perUnit: 3_600_000, until: 24 },
  { unit: 'day' as const, perUnit: 86_400_000, until: 7 },
]

/** Narrow ("5m ago") everywhere a timestamp trails a row — it reads as metadata, not content. */
const relativeFormatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'always', style: 'narrow' })

export function formatTimeAgo(dateString: string | Date): string {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString
  const elapsedMs = Date.now() - date.getTime()

  if (elapsedMs < 60_000) return 'Just now'

  for (const { unit, perUnit, until } of TIME_AGO_RUNGS) {
    const count = Math.floor(elapsedMs / perUnit)
    if (count < until) return relativeFormatter.format(-count, unit)
  }

  // Past a week, "7 days ago" stops being intuitive ("200 days ago"), so show
  // the absolute date instead (e.g. "Jan 16, 2026").
  return absoluteDateFormatter.format(date)
}

function isUrl(value: string): boolean {
  return value.startsWith('http://') || value.startsWith('https://')
}

export function isKnownMeetingAppName(value: string | undefined): value is MeetingAppName {
  return value != null && KNOWN_MEETING_APPS.includes(value as MeetingAppName)
}

const ACTION_TYPE_PROVIDER: Record<string, string> = {
  createLinearTicket: 'linear',
  sendSlackMessage: 'slack',
  createNotionPage: 'notion',
}

const LOGO_TOOLSETS = new Set([
  'gmail',
  'github',
  'google-calendar',
  'google_calendar',
  'microsoft-calendar',
  'microsoft-outlook',
  'outlook-calendar',
  'outlook_calendar',
])

/** Toolsets with variants (`linear-foo`) that share their provider's mark. */
const LOGO_TOOLSET_PREFIXES = new Set(['linear', 'slack', 'notion'])

function toolSetLogoId(toolSet: string | undefined): string | undefined {
  if (!toolSet) return undefined
  if (LOGO_TOOLSETS.has(toolSet)) return toolSet
  const prefix = toolSet.split('-')[0]
  return LOGO_TOOLSET_PREFIXES.has(prefix) ? prefix : undefined
}

/**
 * Returns the appropriate icon for a staged action based on its type.
 * Checks view.icon URL, then actionType, then toolSet, then falls back to HighlightIcon.
 */
export function getActionIcon(action: ActionIconSource, size = 14): ReactNode {
  // Sentinel icon for auto-mode plan actions — render a check-mark glyph.
  if (action.view?.icon === 'check') {
    return <Check size={size} />
  }

  if (action.view?.icon && isUrl(action.view.icon)) {
    return (
      // Attributes, not an inline style, so a well (`Cell.Icon`, `Logo.Well`) can size it like a logo.
      <img
        src={action.view.icon}
        alt=""
        width={size}
        height={size}
        className="corner-smooth rounded-md object-contain"
      />
    )
  }

  const logo =
    providerLogo(ACTION_TYPE_PROVIDER[action.actionType ?? '']) ?? providerLogo(toolSetLogoId(action.toolSet))
  if (logo) return <Logo name={logo} size={size} />

  if (action.toolSet) return <CatalogToolSetIcon toolSet={action.toolSet} size={size} />
  return <FallbackActionIcon size={size} />
}

/**
 * Toolsets without a bespoke glyph (MCP-backed ones like Ashby or HubSpot
 * stage through the backend's icon-less fallback view) borrow their provider
 * catalog icon. Toolset names are snake_case; provider ids are kebab-case.
 */
function CatalogToolSetIcon({ toolSet, size }: { toolSet: string; size: number }): ReactNode {
  const { data: providers } = useProviders()
  const providerId = toolSet.replace(/_/g, '-')
  const provider = providers?.find((p) => p.id === providerId)
  return provider ? <ProviderIcon provider={provider} size={size} /> : <FallbackActionIcon size={size} />
}

function FallbackActionIcon({ size }: { size: number }): ReactNode {
  // currentColor, not the component's white default — this fallback renders on
  // theme-colored surfaces (chat cells, the actions panel), where a fixed white
  // glyph disappears in light mode.
  return (
    <HighlightLogo size={size} aria-hidden>
      <HighlightLogo.Icon />
    </HighlightLogo>
  )
}

/**
 * Returns the title for a staged action
 */
export function getActionTitle(action: StagedAction): string {
  return action.view?.title || action.title || 'Action'
}

/**
 * Returns the title for a recent activity item
 */
export function getActivityTitle(item: RecentActivity): string {
  if (item.type === 'chat') {
    return item.title || 'New Conversation'
  }
  if (item.type === 'audio-note') {
    return item.title || 'Audio note'
  }
  if (item.type === 'completed-action') {
    return item.title || 'Completed Action'
  }
  return 'Activity'
}
